import { Box, Center, Divider, Flex, Text } from "@chakra-ui/react";
import parse from "html-react-parser";
import { Interweave } from "interweave";
import dynamic from "next/dynamic";
import { useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import toaster from "toasted-notes";
import { NextSeo } from "next-seo";
import { each, find, uniqBy, flatten } from "lodash";

import { SocialComponent } from "../../src/components/blog/blogComponent";
import { interweaveTransform } from "../../src/components/blog/processingNodes";
import Error from "../../src/components/common/error";
import ErrorToaster from "../../src/components/common/error/errorToaster";
import { wrapper } from "../../src/redux/store";

import { fetchProductCategories } from "../../src/redux/actions";
import {
  fetchAllBlogs,
  fetchBlogDetails,
} from "../../src/services/blogService";
import { fetchProductsBySearchString } from "../../src/services/products";

const HorizontalProductList = dynamic(() =>
  import("../../src/components/home/horizontalProductList")
);

export default function BlogDetails({ blog, errorMessage }) {
  if (errorMessage) {
    return <Error errorMessage={errorMessage} />;
  }
  const { cart } = useSelector((state) => state);
  const cartProductIds = cart.map((product) => product.id);

  const [products, setProducts] = useState([]);

  const getSearchResults = async (tagNames) => {
    try {
      const requests = tagNames.map(
        (tagName) =>
          new Promise(async (resolve) => {
            const query = `${process.env.NEXT_PUBLIC_NEXTJS_API_URL}/product/search?search=${tagName}`;
            fetchProductsBySearchString(query)
              .then((response) => {
                resolve(response.data);
              })
              .catch((err) => {
                toaster.notify(<ErrorToaster errorMessage={err.message} />, {
                  duration: 3000,
                  position: "top",
                });
              });
          })
      );

      const productArrays = await Promise.all(requests);
      const products = uniqBy(flatten(productArrays), "id");

      each(products, (product) => {
        const metaData = product.meta_data;

        const productRules = find(metaData, { key: "_product_rules" });
        const ruleValue = find(productRules.value, {
          rule_name: "doctorsPrice",
        });
        product.doctor_price = ruleValue.rule_value;

        const oldPrice = find(metaData, { key: "_old_price" });
        product.old_price = oldPrice.value;

        return product;
      });

      setProducts(products);
    } catch (err) {
      toaster.notify(<ErrorToaster errorMessage={err.message} />, {
        duration: 3000,
        position: "top",
      });
    }
  };

  useEffect(() => {
    const tagNames = [];
    if (blog && blog.metaData) {
      const metaData = blog.metaData;
      metaData["TAGS"].forEach((tag) => {
        tagNames.push(tag.name);
      });
    }
    if (tagNames.length > 0) {
      getSearchResults(tagNames);
    }
  }, []);

  let ogTags = [];
  if (blog.metaData && blog.metaData.TAGS) {
    ogTags = blog.metaData.TAGS.map((tag) => tag.name);
  }

  return (
    <>
      <NextSeo
        openGraph={{
          title: parse(blog.title.rendered),
          description: blog.excerpt.rendered.replace(/<[^>]+>/g, ""),
          url: `${process.env.NEXT_PUBLIC_NEXTJS_URL}/blogs/${blog.slug}`,
          type: "article",
          article: {
            publishedTime: blog.date,
            modifiedTime: blog.modified,
            tags: ogTags,
          },
          images:
            blog.metaData && blog.metaData.FEATURE_MEDIA
              ? [
                  {
                    url: blog.metaData.FEATURE_MEDIA.sourceUrl,
                    width: blog.metaData.FEATURE_MEDIA.width,
                    height: blog.metaData.FEATURE_MEDIA.height,
                    alt: blog.metaData.FEATURE_MEDIA.alt,
                  },
                ]
              : [],
        }}
      />
      <Flex w="100%" height="fit-content" direction="column">
        <Center>
          <Box
            mt={{ base: "25px", lg: "50px", "2xl": "75px" }}
            width={{ base: "90%", "2xl": "1350px" }}
          >
            <Flex
              width="100%"
              flexWrap="wrap"
              flexDirection={"column"}
              justifyContent="center"
              alignItems={"center"}
            >
              {blog && (
                <Box width={{ base: "95%", md: "75%", xl: "65%" }}>
                  <Text variant="aboutUsHeading" fontSize="30px">
                    {parse(blog.title.rendered)}
                  </Text>
                  <Divider mt="3%" />
                  {blog && <SocialComponent date={blog.date} />}
                  <Divider />
                  <Interweave
                    transform={interweaveTransform}
                    content={blog.content.rendered}
                  />
                </Box>
              )}
            </Flex>
            <Text mt={{ base: "5%", lg: "10%" }} variant="aboutUsHeading">
              Related Products
            </Text>
            <HorizontalProductList
              products={products}
              cartProductIds={cartProductIds}
            />
          </Box>
        </Center>
      </Flex>
    </>
  );
}

export const getStaticProps = wrapper.getStaticProps(
  (store) =>
    async ({ params }) => {
      const { slug } = params;
      try {
        await store.dispatch(fetchProductCategories());
        const blog = await fetchBlogDetails(slug);

        if (blog.length === 0) {
          return {
            props: { blog: {} },
          };
        }

        return {
          props: { blog: blog[0] },
        };
      } catch (err) {
        return {
          props: {
            blog: {},
            errorMessage: err.message,
          },
        };
      }
    }
);

export async function getStaticPaths() {
  try {
    const blogs = await fetchAllBlogs();
    const paths = blogs.map(({ id, slug }) => ({
      params: { slug },
    }));

    return {
      paths,
      fallback: false,
    };
  } catch (err) {
    console.log(err);
    return { paths: [], fallback: false };
  }
}
